import React from 'react';
import {DialogType} from 'components/FluentUICompat';
import {BaseDialog} from 'components/Dialogs/BaseDialog';
import {useDialogState} from 'components/Dialogs';
import {useCommand} from 'hooks/useCommand';
import {useParagraphsContext} from 'features/RegulatoryDocuments/components/DocumentDetails/ParagraphsContext';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';
import {useRemoveRequirementAssignedParagraphsMutation} from '../hooks/requirements.generated';
import {RequirementDetailsDocument} from '../hooks/requirementDetails.generated';
import {RequirementParagraphRefInput, RequirementStatus} from 'types';
import {useUserContext} from 'authentication/UserContext';

interface DeleteSegmentCommandProps {
	requirementId: string;
	requirementStatus: RequirementStatus;
}

export const DeleteSegmentCommand: React.FC<DeleteSegmentCommandProps> = ({
	requirementId,
	requirementStatus,
}) => {
	const {t} = useRequirementsTranslation();
	const {selectedParagraphs, setSelectedParagraphs} = useParagraphsContext();
	const [dialogState, showDialog, hideDialog] = useDialogState();
	const {isAdmin, isVex, isVexSupport} = useUserContext();

	const [removeAssignedParagraphs] =
		useRemoveRequirementAssignedParagraphsMutation({
			refetchQueries: [RequirementDetailsDocument],
		});

	const handleConfirm = React.useCallback(async () => {
		const changes = Object.values(
			selectedParagraphs.reduce<Record<string, RequirementParagraphRefInput>>(
				(acc, paragraph) => {
					const {regulation} = paragraph.parent;
					if (!regulation) {
						return acc;
					}

					const regdocId = paragraph.parent.id;
					if (!acc[regdocId]) {
						acc[regdocId] = {
							regulationRef: {id: regulation.id},
							regdocRef: {id: regdocId},
							paragraphRefs: [],
						};
					}

					acc[regdocId].paragraphRefs.push({id: paragraph.id});
					return acc;
				},
				{},
			),
		);

		await removeAssignedParagraphs({
			variables: {requirementId, changes},
		});

		setSelectedParagraphs([]);
		hideDialog();
	}, [
		selectedParagraphs,
		requirementId,
		removeAssignedParagraphs,
		setSelectedParagraphs,
		hideDialog,
	]);

	const hidden = React.useMemo(() => {
		if (isAdmin || isVex) {
			return false;
		}
		if (isVexSupport) {
			return requirementStatus !== RequirementStatus.ReadyForApproval;
		}
		return true;
	}, [isAdmin, isVex, isVexSupport, requirementStatus]);

	useCommand(
		{
			key: 'deleteSegment',
			priority: 3,
			text: t('DeleteSegment'),
			iconProps: {iconName: 'Delete'},
			disabled: selectedParagraphs.length === 0,
			onClick: showDialog,
			hidden,
		},
		[t, showDialog, hidden, selectedParagraphs],
	);

	return (
		<BaseDialog
			{...dialogState}
			onConfirm={handleConfirm}
			confirmText={t('DeleteSegmentDialog.Confirm')}
			cancelText={t('DeleteSegmentDialog.Cancel')}
			dialogContentProps={{
				type: DialogType.normal,
				title: t('DeleteSegmentDialog.Title'),
				subText: t('DeleteSegmentDialog.Subtext'),
			}}
		/>
	);
};
